import { Link } from "react-router";
import { motion, useScroll } from "framer-motion";
import { BrandLogo } from "@rekati/ui";
import IconArrow from "~icons/solar/arrow-right-up-linear";
import { IFORTE_LOGO } from "../../assets/brand";
import { useI18n } from "../../content/i18n";
import { useAttendeeSession } from "../attendee-session/session-store";
import { LanguageToggle } from "../shell/LanguageToggle";
import { scrollToSection } from "../shell/smooth-scroll";
import { useBrandQuery } from "./queries";

const SECTIONS = ["about", "theme", "speakers", "perks", "venue"] as const;

/**
 * A pill that stays put at the top of the page. Its one moving part is the thin
 * yellow line along its foot: how far down the page you are.
 */
export function EventNav() {
  const { t } = useI18n();
  const brand = useBrandQuery().data;
  const signedIn = Boolean(useAttendeeSession());
  // Lenis already eases the scroll, so the line follows it directly.
  const { scrollYProgress } = useScroll();

  return (
    <header className="fixed inset-x-0 top-0 z-40 flex justify-center px-3 pt-3 sm:pt-4">
      <div className="relative flex w-full max-w-6xl items-center gap-2 rounded-full border border-border bg-background/90 py-1.5 pr-1.5 pl-4 shadow-soft backdrop-blur-md sm:gap-4 sm:pl-5">
        <Link
          to="/"
          className="-m-1 shrink-0 rounded-sm p-1"
          aria-label={brand?.organizer_name ? `${brand.organizer_name}: home` : "Home"}
        >
          <BrandLogo
            src={brand?.organizer_logo_url || brand?.organizer_logo_dark_url}
            fallbackSrc={IFORTE_LOGO}
            name={brand?.organizer_name || "iForte"}
            className="h-6"
            textClassName="text-base font-bold"
          />
        </Link>
        <nav aria-label={t("nav.label")} className="hidden flex-1 justify-center md:flex">
          <ul role="list" className="flex items-center gap-1">
            {SECTIONS.map((id) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(id);
                  }}
                  className="rounded-full px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  {t(`nav.${id}`)}
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
          <Link
            to="/rsvp"
            className="hidden rounded-full px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground lg:block"
          >
            {signedIn ? t("flow.done.manage") : t("returning.kicker")}
          </Link>
          <LanguageToggle label={t("meta.language")} className="border-border" />
          <Link
            to="/register"
            className="inline-flex h-10 items-center gap-1.5 rounded-full bg-foreground pr-2 pl-4 text-sm max-[360px]:pr-4 font-semibold text-background transition-colors hover:bg-foreground/90"
          >
            {t("nav.register")}
            {/* The arrow chip gives way on the narrowest phones so the bar keeps to one row. */}
            <span className="grid size-7 place-items-center rounded-full bg-volt text-volt-foreground max-[360px]:hidden">
              <IconArrow className="size-4" aria-hidden="true" />
            </span>
          </Link>
        </div>
        {/* Inset from the rounded ends so the line reads as a track, not a clipped edge. */}
        <span aria-hidden="true" className="absolute inset-x-6 -bottom-px h-0.5 overflow-hidden rounded-full">
          <motion.span
            className="block h-full origin-left rounded-full bg-volt"
            style={{ scaleX: scrollYProgress }}
          />
        </span>
      </div>
    </header>
  );
}
